import React, { useEffect, useState } from "react";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { GuidanceUser } from "../../index";
import Styles from "./styles.module.css";
import { minLikesOf } from "../../config";
import usePageBackground from "../../usePageBackground";

type Props = {
  userInfo: GuidanceUser;
  setUserInfo: React.Dispatch<React.SetStateAction<GuidanceUser>>;
  rankCount: number;
  onNext: () => void;
};

// info/schoolInfo の学校のエントリの departments(コース → 学年 → クラス)。アプリの学校登録(5.account/1.items/3.school.dart)と同じデータ。
type Departments = Record<string, { grades?: Record<string, { classes?: string[] }> }>;

// 学年は「1年生」「2年生」…の順に並べる(保存されている順はばらばらのため)
const sortGrades = (grades: string[]) =>
  [...grades].sort((a, b) => (parseInt(a, 10) || 99) - (parseInt(b, 10) || 99) || a.localeCompare(b, "ja"));

// 学校ごとの選択肢の読み込み結果(null は選択肢なし=自由入力)。検索画面から戻ったときに読み直さないよう、ページを開いているあいだ覚えておく。
const departmentsCache = new Map<string, Promise<Departments | null>>();

const loadDepartments = (school: string) => {
  let p = departmentsCache.get(school);
  if (!p) {
    // 電波が悪いなどで返ってこないときに「読み込み中」のまま進めなくならないよう、8秒で諦めて自由入力にする
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("schoolInfo timeout")), 8000));
    p = Promise.race([getDoc(doc(getFirestore(), "info", "schoolInfo")), timeout])
      .then((snap) => {
        const deps = snap.data()?.[school]?.departments as Departments | undefined;
        return deps && Object.keys(deps).length > 0 ? deps : null;
      })
      .catch((e) => {
        console.error(e);
        departmentsCache.delete(school); // 失敗したときは次に開いたときに読み直す
        return null;
      });
    departmentsCache.set(school, p);
  }
  return p;
};

export default function GuidanceDesc({ userInfo, setUserInfo, rankCount, onNext }: Props) {

  const [step, setStep] = useState<"form" | "confirm" | "done">("form");
  const [loading, setLoading] = useState(false);
  usePageBackground("#FAFAFA");

  // 情報入力 → 参加状況の確認 → 完了 と切り替わったら一番上から見せる
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const { grade, className, course, name, number } = userInfo;

  // コース・学年・クラスの選択肢。読み込みが終わるまでは「読み込み中」を出し、終わってから選択式か自由入力かを決める
  // (以前は読み込み中に自由入力の欄を出していたため、遅いと自由入力 → 選択式に切り替わって見えていた)。
  // 読めない・登録が無い学校は、今までどおり自由入力にする。
  const [ departments, setDepartments ] = useState<Departments | null | undefined>(undefined); // undefined は読み込み中
  useEffect(() => {
    let cancelled = false;
    loadDepartments(userInfo.school).then((deps) => {
      if (!cancelled) setDepartments(deps);
    });
    return () => { cancelled = true; };
  }, [userInfo.school]);

  const courseOptions = departments ? Object.keys(departments) : [];
  const gradeOptions = departments && departments[course] ? sortGrades(Object.keys(departments[course].grades ?? {})) : [];
  const classOptions = departments && departments[course]?.grades?.[grade] ? departments[course].grades![grade].classes ?? [] : [];

  // 選択式のときは、選択肢にある値だけ有効にする(自由入力のときの値などが残っていても進めないように)
  const selectValid = !departments || (
    courseOptions.includes(course) && gradeOptions.includes(grade) && classOptions.includes(className)
  );

  const canNext =
    number.trim() !== "" &&
    grade.trim() !== "" &&
    className.trim() !== "" &&
    course.trim() !== "" &&
    name.trim() !== "" &&
    departments !== undefined &&
    selectValid;

  const update = (key: keyof GuidanceUser, value: string) => {
    setUserInfo((prev) => ({ ...prev, [key]: value }));
  };

  async function handleReport() {
    try {
      setLoading(true);
      const db = getFirestore();
      const uid = `${userInfo.school}_${userInfo.grade}_${userInfo.className}_${userInfo.course}_${userInfo.name}`.replace(/\s/g, "");
      await setDoc(doc(db, "guidance", uid), {
        school: userInfo.school,
        grade: userInfo.grade,
        className: userInfo.className,
        course: userInfo.course,
        name: userInfo.name,
        number: userInfo.number,
        report: true,
        updateTime: serverTimestamp(),
      }, { merge: true });
      setStep("done");
    } catch (e) {
      console.error(e);
      alert("保存に失敗しました。再度お試しください。");
    } finally {
      setLoading(false);
    }
  }

  if (step === "done") {
    return (
      <div className={`guidance-page ${Styles.done_page}`}>
        <div className={Styles.done_card}>
          <div className={Styles.done_title}>完了</div>
          <p className={Styles.done_text}>
            内定承諾の登録が完了しました。
            <br />
            ありがとうございました。
          </p>
        </div>
      </div>
    );
  }

  if (step === "confirm") {
    return (
      <div className={`guidance-page ${Styles.page}`}>
        <div className={Styles.container}>
          {/* ‹ と「ガイダンス」。どちらを押しても学生情報入力に戻る(入力した内容は残る) */}
          <div className={Styles.header}>
            <button className={Styles.header_back} onClick={() => setStep("form")} aria-label="学生情報入力に戻る">
              <BackIcon />
              <span className={Styles.header_title}>ガイダンス</span>
            </button>
          </div>

          {/* 参加状況の確認(最初の画面の「校内ガイダンスの準備をしよう」と同じピンクのカード) */}
          <div className={Styles.intro}>
            <div className={Styles.intro_head}>
              <FlowerIcon />
              <div className={Styles.intro_title}>参加状況の確認</div>
            </div>
            <p className={Styles.intro_text}>あてはまる方を、下のボタンから選んでください。</p>
            <div className={Styles.intro_badge}>どちらかを選択</div>
            <Step
              number="A"
              title="ガイダンスに参加する"
              text="当日のガイダンスで話を聞きたいサロンを、このあと選んでいきます。"
            />
            <Step
              number="B"
              title="内定承諾済み"
              text="すでに内定を承諾している方は、こちらを選んでください。"
            />
          </div>

          <div className={Styles.confirm_btns}>
            <button
              onClick={onNext}
              className={Styles.start_btn}
            >
              ガイダンスに参加する
            </button>
            <button
              onClick={handleReport}
              disabled={loading}
              className={`${Styles.report_btn} ${loading ? Styles.start_btn_disabled : ""}`}
            >
              {loading ? "保存中..." : "内定承諾済み"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`guidance-page ${Styles.page}`}>
      <div className={Styles.container}>

        {/* header */}
        <div className={Styles.header}>
          <div className={Styles.header_title}>ガイダンス</div>
        </div>

        {/* 目的と使い方(学生アプリの 1.desc.dart の「校内ガイダンスの準備をしよう」と同じ) */}
        <div className={Styles.intro}>
          <div className={Styles.intro_head}>
            <FlowerIcon />
            <div className={Styles.intro_title}>校内ガイダンスの準備をしよう</div>
          </div>
          <p className={Styles.intro_text}>
            当日、ガイダンスで話を聞きたいサロンを事前に回答していただくための仕組みです。{"\n"}
            求人情報を見ながら、気になるサロンを選んでいきましょう。
          </p>
          <div className={Styles.intro_badge}>やることは2ステップ</div>
          <Step
            number="1"
            title="気になるサロンを選ぶ"
            text={`サロンの ♡ を ${minLikesOf(rankCount)}個以上 押します。上限はないので、良さそうなサロンはどんどん押してOK！`}
          />
          <Step
            number="2"
            title="志望の順位をつける"
            text={rankCount > 0
              ? `選んだ中から、話を聞きたい順に第1志望〜第${rankCount}志望まで順番をつけます。`
              : "選んだ中から、ぜひ話したいサロンを第1志望に3つ、話せたら嬉しいサロンを第2志望に3つ選びます。"}
          />
          <div className={Styles.intro_tip}>
            <BulbIcon />
            <span>迷ったら、写真の雰囲気や「なんとなく気になる」で選んで大丈夫です。</span>
          </div>
        </div>

        {/* user info */}
        <div className={`${Styles.card} ${Styles.card_last}`}>
          <div className={Styles.card_title}>学生情報入力</div>
          <div className={Styles.field_list}>
            <Input label="出席番号" value={number} onChange={(v) => update("number", v)} placeholder="5" />
            {departments === undefined ? (
              <>
                <Select label="コース" value="" options={[]} disabled loading />
                <Select label="学年" value="" options={[]} disabled loading />
                <Select label="クラス" value="" options={[]} disabled loading />
              </>
            ) : departments ? (
              <>
                {/* 上を選び直したら、下は選び直してもらう */}
                <Select label="コース" value={course} options={courseOptions}
                  onChange={(v) => setUserInfo((p) => ({ ...p, course: v, grade: "", className: "" }))} />
                <Select label="学年" value={grade} options={gradeOptions} disabled={!course}
                  onChange={(v) => setUserInfo((p) => ({ ...p, grade: v, className: "" }))} />
                <Select label="クラス" value={className} options={classOptions} disabled={!grade}
                  onChange={(v) => update("className", v)} />
              </>
            ) : (
              <>
                <Input label="コース" value={course} onChange={(v) => update("course", v)} placeholder="美容師学科" />
                <Input label="学年" value={grade} onChange={(v) => update("grade", v)} placeholder="2年" />
                <Input label="クラス" value={className} onChange={(v) => update("className", v)} placeholder="1組" />
              </>
            )}
            <Input label="名前" value={name} onChange={(v) => update("name", v)} placeholder="山田 太郎" />
          </div>
        </div>

        <button
          onClick={() => setStep("confirm")}
          disabled={!canNext}
          className={`${Styles.start_btn} ${!canNext ? Styles.start_btn_disabled : ""}`}
        >
          利用開始
        </button>
      </div>
    </div>
  );
}

function Input({
  label, value, onChange, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <div className={Styles.field_label}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={Styles.field_input}
      />
    </div>
  );
}

function Select({
  label, value, options, disabled, loading, onChange,
}: {
  label: string;
  value: string;
  options: string[];
  disabled?: boolean;
  loading?: boolean;
  onChange?: (v: string) => void;
}) {
  return (
    <div>
      <div className={Styles.field_label}>{label}</div>
      <select
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        className={`${Styles.field_input} ${Styles.field_select} ${value ? "" : Styles.field_select_empty}`}
      >
        <option value="">{loading ? "読み込み中…" : "選択してください"}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Step({ number, title, text }: { number: string; title: string; text: string }) {
  return (
    <div className={Styles.step_row}>
      <div className={Styles.step_number}>{number}</div>
      <div>
        <div className={Styles.step_title}>{title}</div>
        <div className={Styles.step_text}>{text}</div>
      </div>
    </div>
  );
}

const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="m15 5-7 7 7 7" />
  </svg>
);

const FlowerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#E44E70" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="6.5" r="3.5" /><circle cx="17.2" cy="10.3" r="3.5" /><circle cx="15.2" cy="16.4" r="3.5" />
    <circle cx="8.8" cy="16.4" r="3.5" /><circle cx="6.8" cy="10.3" r="3.5" /><circle cx="12" cy="12" r="2.6" fill="#FFD3DE" />
  </svg>
);

const BulbIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E44E70" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.2h5c0-.9.4-1.7 1.1-2.2A6 6 0 0 0 12 3z" />
  </svg>
);
